import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InspectionQuestion, InspectionWorkflow, WorkflowStep, IterateStep } from "src/components/pages/configurationDrivenInspections/types/inspectionWorkflow";
import { apiRequest } from "src/utils/apiRequest";
import type { RootState } from "src/store/store";

export type WorkflowState = {
    workflows: InspectionWorkflow[];
    activeWorkflow: InspectionWorkflow | null;
    questions: InspectionQuestion[];
    showWorkflowHeader: boolean;
    currentStep: WorkflowStep | IterateStep | null;
    currentIterateStep: WorkflowStep | null;
    shownHumanText: string;
    inspectionId: string;
    currentCategoryIndex: number;
    currentStepIndex: number;
    workflowComplete: boolean; // True only when advanceStep reaches the end
    currentIterationIndex: number; // 0-based index of current iteration
    totalIterations: number; // Total number of iterations (drum count)
    scheduledWorkflowLoading: boolean; // True while getScheduledWorkflow is in flight
    scheduledWorkflowError: boolean; // True when getScheduledWorkflow failed
    resumeIterateStepIndex: number; // sub-step index IterateStep should start on
    resumeCompletedContainerCount: number; // container count IterateStep should start on (header)
    resumePending: boolean; // One-shot flag: draft was hydrated; executor should skip its mount reset once
    // containerId -> sourceInspectionId, for containers whose label capture step can be
    // skipped this inspection. Populated only when getScheduledWorkflow was called with a siteCode; empty otherwise.
    skippableContainers: Record<string, string>;
    // Last user-chosen flashlight (torch) state for the current inspection. Client-only;
    // reset to false on new inspection and on resume.
    flashlightEnabled: boolean;
};

const initialState: WorkflowState = {
    workflows: [],
    activeWorkflow: null,
    questions: [],
    showWorkflowHeader: true,
    currentStep: null,
    currentIterateStep: null,
    shownHumanText: "",
    inspectionId: '', // Will be set when workflow inspection is created
    currentCategoryIndex: 0,
    currentStepIndex: 0,
    workflowComplete: false,
    currentIterationIndex: 0,
    totalIterations: 0,
    scheduledWorkflowLoading: false,
    scheduledWorkflowError: false,
    resumeIterateStepIndex: 0,
    resumeCompletedContainerCount: 0,
    resumePending: false,
    skippableContainers: {},
    flashlightEnabled: false,
}

export const getInspectionWorkflow = createAsyncThunk(
    "inspectionWorkflow/getInspectionWorkflow",
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiRequest({url: "/inspections/workflow-config", method: "GET" });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch inspection workflow");
        }
    }
);

export const getScheduledWorkflow = createAsyncThunk(
    "workflow/getScheduledWorkflow",
    async (scheduledId: string, { getState, rejectWithValue }) => {
        try {
            // activeSiteCode always lives in userSlice; read it here rather than
            // requiring every caller to pass it through. Sent as-is (including
            // null/undefined) -- the backend treats a missing siteCode as "no skip
            // behavior" rather than an error, so no client-side guard is needed.
            const siteCode = (getState() as RootState).user.activeSiteCode;
            const data = await apiRequest({
                url: `/inspections/scheduled/${scheduledId}/workflow`,
                method: "GET",
                params: { siteCode },
            });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch scheduled workflow");
        }
    }
);

export const createWorkflowInspection = createAsyncThunk(
    "workflow/createWorkflowInspection",
    async (
        { templateId, templateName, siteCode, austinInspectionId }: { templateId: string; templateName: string; siteCode?: string; austinInspectionId?: string },
        { getState, rejectWithValue }
    ) => {
        try {
            // Snapshot the questions the inspector was shown, so the record does not depend on
            // a question bank that is edited in place. Austin only: their workflow is generated
            // per scheduled inspection, so the backend cannot derive the set from the
            // templateId. On the template-driven path this same field holds the entire bank in
            // bank order, not the template's subset, so the backend resolves that one itself.
            const { questions } = (getState() as RootState).workflow;
            const sendQuestions = Boolean(austinInspectionId) && questions.length > 0;
            const data = await apiRequest({
                url: "/inspections",
                method: "POST",
                data: {
                    templateId,
                    templateName,
                    siteCode: siteCode || "ABE2",
                    // Link to the Austin scheduled inspection so results can be
                    // synced back to RCMS when the inspection is submitted.
                    ...(austinInspectionId ? { austinInspectionId } : {}),
                    ...(sendQuestions ? { questions } : {}),
                },
            });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create workflow inspection");
        }
    }
);

/** Inspection Template Slice */
const { reducer, actions } = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setShowWorkflowHeader: (state, { payload }) => {
      state.showWorkflowHeader = payload;
    },
    setShownHumanText: (state, { payload }) => {
      state.shownHumanText = payload;
    },
    setCurrentStep: (state, { payload }) => {
      state.currentStep = payload;
    },
    setCurrentIterateStep: (state, { payload }) => {
      state.currentIterateStep = payload;
    },
    setIterationProgress: (state, { payload }: { payload: { currentIndex: number; total: number } }) => {
      state.currentIterationIndex = payload.currentIndex;
      state.totalIterations = payload.total;
    },
    setActiveWorkflow: (state, { payload }: { payload: InspectionWorkflow }) => {
      state.activeWorkflow = payload;
    },
    /**
     * Atomically advances the workflow to the next step. Moves to the next step
     * within the current category, or to the first step of the next category,
     * or marks the workflow complete (currentStep = null) if there are no more
     * steps/categories.
     */
    advanceStep: (state) => {
      const workflow = state.activeWorkflow;
      if (!workflow) return;

      const category = workflow.categories[state.currentCategoryIndex];

      if (state.currentStepIndex < category.steps.length - 1) {
        // Next step in same category
        state.currentStepIndex += 1;
        state.currentStep = category.steps[state.currentStepIndex];
      } else if (state.currentCategoryIndex < workflow.categories.length - 1) {
        // Next category
        state.currentCategoryIndex += 1;
        state.currentStepIndex = 0;
        state.currentStep = workflow.categories[state.currentCategoryIndex].steps[0];
      } else {
        // Workflow complete
        state.currentStep = null;
        state.currentIterateStep = null;
        state.workflowComplete = true;
      }
    },
    /**
     * Resets workflow position to the first step of the first category.
     * Used when starting/restarting a workflow.
     */
    resetWorkflowPosition: (state) => {
      const workflow = state.activeWorkflow;
      state.currentCategoryIndex = 0;
      state.currentStepIndex = 0;
      state.workflowComplete = false;
      state.resumeIterateStepIndex = 0;
      state.resumeCompletedContainerCount = 0;
      state.resumePending = false; // defensive: a normal reset clears any pending resume
      if (workflow && workflow.categories.length > 0) {
        state.currentStep = workflow.categories[0].steps[0];
      } else {
        state.currentStep = null;
      }
    },
    /**
     * Hydrates the executor state from an existing draft inspection. Sets the
     * inspection id and the resume position (category/step, plus optional
     * IterateStep sub-step/iteration) and clears the completion flag so the
     * workflow can continue from where it left off.
     */
    setFlashlightEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.flashlightEnabled = payload;
    },
    hydrateFromDraft: (
      state,
      { payload }: PayloadAction<{
        inspectionId: string;
        categoryIndex: number;
        stepIndex: number;
        iterateStepIndex?: number;
        completedContainerCount?: number;
      }>
    ) => {
      const workflow = state.activeWorkflow;
      if (!workflow) return;
      state.inspectionId = payload.inspectionId;
      state.workflowComplete = false;
      state.currentCategoryIndex = payload.categoryIndex;
      state.currentStepIndex = payload.stepIndex;
      state.currentStep = workflow.categories[payload.categoryIndex]?.steps[payload.stepIndex] ?? null;
      state.resumeIterateStepIndex = payload.iterateStepIndex ?? 0;
      state.resumeCompletedContainerCount = payload.completedContainerCount ?? 0;
      state.resumePending = true;
      state.flashlightEnabled = false; // resumed inspection starts with flashlight off
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getInspectionWorkflow.fulfilled,
      (state, { payload }) => {
        state.workflows = payload.workflows ?? [];
        state.questions = payload.questions;
      }
    );
    
    builder.addCase(getScheduledWorkflow.pending, (state) => {
      // Clear any stale workflow so the UI shows a loading state instead of
      // previous/empty data while the request is in flight.
      state.scheduledWorkflowLoading = true;
      state.scheduledWorkflowError = false;
      state.activeWorkflow = null;
      state.questions = [];
      state.skippableContainers = {};
    });

    builder.addCase(
      getScheduledWorkflow.fulfilled,
      (state, { payload }) => {
        state.activeWorkflow = payload.workflow;
        state.questions = payload.questions ?? [];
        state.skippableContainers = payload.skippableContainers ?? {};
        state.scheduledWorkflowLoading = false;
        state.scheduledWorkflowError = false;
      }
    );

    builder.addCase(getScheduledWorkflow.rejected, (state) => {
      state.scheduledWorkflowLoading = false;
      state.scheduledWorkflowError = true;
    });

    builder.addCase(
      createWorkflowInspection.fulfilled,
      (state, { payload }) => {
        state.inspectionId = payload.inspectionId;
        // A newly created inspection starts clean: reset both the resume-seed
        // state and the workflow position so it can't inherit a prior inspection's
        // terminal position (which would misroute the executor to review or index
        // out of range against the new workflow and crash the render).
        state.resumePending = false;
        state.resumeCompletedContainerCount = 0;
        state.resumeIterateStepIndex = 0;
        state.workflowComplete = false;
        state.currentCategoryIndex = 0;
        state.currentStepIndex = 0;
        state.currentStep = state.activeWorkflow?.categories[0]?.steps[0] ?? null;
        state.currentIterateStep = null;
        state.flashlightEnabled = false; // new inspection starts with flashlight off
      }
    );
  },
});

export const { setShowWorkflowHeader, setShownHumanText, setCurrentStep, setCurrentIterateStep, setIterationProgress, setActiveWorkflow, advanceStep, resetWorkflowPosition, hydrateFromDraft, setFlashlightEnabled } = actions;

export default reducer;
